import { useCallback } from "react";
import { Offcanvas } from "react-bootstrap";
import type { EditableTask, Task } from "../../../api/src/tasks/tasks.service";
import type { CrmUsersRecord } from "../firm/lib";
import CreateSlantTask, { EditableSlantTask } from "./CreateSlantTask";
import CreateTask, { CreateTaskCrm } from "./CreateTask";

const CreateTaskPane = ({
  show,
  setShow,
  onCreate,
  crm,
  record,
}: {
  show: boolean;
  setShow: (val: boolean) => void;
  onCreate: (data: EditableTask) => Promise<Task | null>;
  crm: CreateTaskCrm;
  // The record this pane was opened on, so the form can ask the server for that
  // record's CRM users. The pane mounts on three surfaces — household, contact
  // and account — and the account passes its household, having no CRM link of
  // its own (s-task-assignee-list-source-019fd301825de871, R1).
  // Undefined only where there is no household to inherit a CRM from — a
  // professional contact. The list then falls back to the firm's derived CRM,
  // which is that page's existing behaviour.
  record?: CrmUsersRecord;
}) => {
  const hidePane = useCallback(() => setShow(false), [setShow]);

  const onCreateInner = useCallback(
    async (data: EditableTask) => {
      const body = await onCreate(data);
      setShow(false);

      return body;
    },
    [onCreate, setShow],
  );

  const onCreateSlant = useCallback(
    // `EditableTask` is the shape all three CRMs are written from and declares
    // `category`, which Slant has no equivalent of. The narrower Slant payload
    // widens to it here rather than carrying a field Slant never receives.
    // `assignedTo` does carry through: `taskBody`
    // (api/src/slant/tasks.service.ts) sends it as `assigned_to_id` when it is
    // one of Slant's string ids.
    (data: EditableSlantTask) => onCreateInner(data as EditableTask),
    [onCreateInner],
  );

  // A household holds exactly one CRM link, and a switched household is
  // Slant-only (the Wealthbox link is deleted, `migration.service.ts`), so this
  // is the ordinary state after a switch. Slant's field set is not the
  // Wealthbox/Redtail one — no category, an extra Urgent priority — so it gets
  // its own form (s-slant-create-task-form-019fd301b735a8cd). A record still
  // linked to Wealthbox or Redtail keeps the shared form, which creates in
  // every CRM the record is linked to.
  const slantOnly = crm.slant && !crm.wealthbox && !crm.redtail;

  return (
    <Offcanvas show={show} onHide={hidePane} placement="end">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Create Task</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {slantOnly ? (
          <CreateSlantTask onCreate={onCreateSlant} record={record} />
        ) : (
          <CreateTask onCreate={onCreateInner} crm={crm} record={record} />
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default CreateTaskPane;
